import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea, inputCls } from "@/components/age-calculator/shared";
import { AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate } from "@/components/age-calculator/premium";

const TITLE = "Dog & Cat Age Calculator: Convert Pet Years to Human Years";
const DESC = "Calculate your pet's real age. Convert dog age and cat age into equivalent human years based on scientific size and growth tracking.";

const FAQS = [
  { q: "Is one dog year really seven human years?", a: "No — that's a myth. Dogs mature very rapidly in the first two years (roughly 15 and 24 human-year equivalents), then age slower thereafter. Large breeds age faster than small breeds." },
  { q: "How do you calculate cat age in human years?", a: "A 1-year-old cat is ~15 human years, a 2-year-old is ~24, then each additional cat year adds about 4 human years. Indoor cats often reach the equivalent of 80–100 human years." },
  { q: "Why does dog size matter?", a: "Giant breeds like Great Danes have shorter lifespans and age faster in their later years, while small breeds like Chihuahuas mature slowly after year two. Our formula weights this." },
  { q: "How can I keep my pet biologically younger?", a: "Weight control, dental care, mental stimulation and yearly vet screenings extend healthy lifespan more than any single supplement — the same principles that lower your own biological age." },
];

function dogHumanAge(petAge: number, size: "small" | "medium" | "large" | "giant") {
  if (petAge <= 0) return 0;
  if (petAge <= 1) return Math.round(15 * petAge);
  if (petAge <= 2) return 15 + Math.round(9 * (petAge - 1));
  const perYear = { small: 4, medium: 5, large: 6, giant: 7 }[size];
  return 24 + Math.round((petAge - 2) * perYear);
}
function catHumanAge(petAge: number) {
  if (petAge <= 0) return 0;
  if (petAge <= 1) return Math.round(15 * petAge);
  if (petAge <= 2) return 15 + Math.round(9 * (petAge - 1));
  return 24 + Math.round((petAge - 2) * 4);
}

export const Route = createFileRoute("/age-calculator/pet-dog-cat")({
  head: () => leafHead({ path: "/age-calculator/pet-dog-cat", title: TITLE, description: DESC, scripts: [buildFaqLd(FAQS), buildBreadcrumbLd([{ name: "Home", url: "https://drhint.com/" }, { name: "Age Calculator", url: "https://drhint.com/age-calculator" }, { name: "Pet Age (Dog & Cat)", url: "https://drhint.com/age-calculator/pet-dog-cat" }])] }),
  component: Page,
});

function Page() {
  const [species, setSpecies] = useState<"dog" | "cat">("dog");
  const [age, setAge] = useState(3);
  const [size, setSize] = useState<"small" | "medium" | "large" | "giant">("medium");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();

  const human = useMemo(() => species === "dog" ? dogHumanAge(age, size) : catHumanAge(age), [species, age, size]);
  const chart = useMemo(() => [1, 2, 3, 5, 7, 10, 13, 16].map((y) => ({ pet: y, human: species === "dog" ? dogHumanAge(y, size) : catHumanAge(y) })), [species, size]);
  const stage = human < 20 ? "Youth" : human < 40 ? "Adult" : human < 60 ? "Mature" : "Senior";
  const meterPct = Math.min(1, human / 100);
  const shareMsg = `🐾 My ${species === "dog" ? "dog" : "cat"} is ${age} years old — equivalent to a ${human}-year-old human (${stage})!\n\nCalculate your pet's human age on https://drhint.com/age-calculator/pet-dog-cat`;

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Pet Age
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="inline-flex rounded-xl border border-border bg-muted p-1 mb-4">
            {(["dog", "cat"] as const).map((s) => (
              <button
                key={s}
                onClick={() => { setSpecies(s); reset(); }}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold capitalize transition ${species === s ? "bg-background text-primary shadow" : "text-muted-foreground"}`}
              >
                {s === "dog" ? "🐶 Dog" : "🐱 Cat"}
              </button>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Pet Age (years)</span>
              <input type="number" min={0} max={30} step="0.5" value={age} onChange={(e) => { setAge(parseFloat(e.target.value) || 0); reset(); }} className={inputCls()} />
            </label>
            {species === "dog" && (
              <label className="flex flex-col gap-1">
                <span className="text-sm font-semibold">Dog Size</span>
                <select value={size} onChange={(e) => { setSize(e.target.value as typeof size); reset(); }} className={inputCls()}>
                  <option value="small">Small (up to 9 kg)</option>
                  <option value="medium">Medium (9–23 kg)</option>
                  <option value="large">Large (23–45 kg)</option>
                  <option value="giant">Giant (45+ kg)</option>
                </select>
              </label>
            )}
          </div>
          <AnalyzeButton onClick={trigger} disabled={analyzing} label="Convert to Human Age" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed ? (
              <>
                <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6 text-center" style={{ minHeight: 140 }}>
                  <div className="text-5xl mb-2">{species === "dog" ? "🐶" : "🐱"}</div>
                  <div className="text-xl md:text-2xl font-bold">
                    Your {age}-year-old {species === "dog" ? `${size} dog` : "cat"} is equivalent to a{" "}
                    <span className="text-primary text-3xl md:text-4xl font-black">{human}-year-old</span> human!
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">Life stage: <span className="font-semibold text-foreground">{stage}</span></div>
                </div>
                <div className="mt-6 rounded-2xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <span>Puppy / Kitten</span><span>Adult</span><span>Senior</span>
                  </div>
                  <div className="relative h-4 rounded-full bg-gradient-to-r from-emerald-300 via-amber-300 to-rose-400 overflow-visible">
                    <div className="absolute -top-1 h-6 w-1 bg-foreground rounded" style={{ left: `${meterPct * 100}%`, transform: "translateX(-50%)" }} />
                  </div>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  <ResultCard label="Equivalent Human Age" value={`${human} yrs`} sub={species === "dog" ? `${size} breed` : "cat"} />
                  <ResultCard label="Life Stage" value={stage} />
                </div>
                <div className="mt-6 overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted"><tr><th className="p-2 text-left">Pet years</th><th className="p-2 text-left">Human-year equivalent</th></tr></thead>
                    <tbody>
                      {chart.map((row) => (
                        <tr key={row.pet} className="border-t border-border">
                          <td className="p-2">{row.pet}</td>
                          <td className="p-2 font-semibold text-primary">{row.human}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <WhatsAppShareButton message={shareMsg} label="Share My Pet's Human Age" />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Tap Convert to reveal your pet's human age.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>
      <ContentSection title="Why one dog year isn't seven human years">
        <p>
          The old "multiply by seven" rule was invented to convince pet owners to bring animals for annual vet visits.
          Modern research — including a 2020 UCSD DNA-methylation study — shows dogs age <strong>extremely fast</strong> in
          the first two years, then slow down. A 1-year-old Labrador is closer to a 15-year-old human than a 7-year-old.
        </p>
        <p>
          Size also matters. A Great Dane's cellular clock runs faster than a Chihuahua's; that's why giant breeds
          typically live 7–8 years while small breeds pass 15. Our calculator applies size-adjusted per-year rates after
          age 2 so the equivalent human age matches modern veterinary charts.
        </p>
        <p>
          Curious about your own biological vs. chronological gap? Try the{" "}
          <Link to="/age-calculator/biological-health-bmi" className="text-primary underline">Biological & Health Age tool</Link> next,
          or return to the main <Link to="/age-calculator" className="text-primary underline">Age Calculator hub</Link>.
        </p>
      </ContentSection>
      <FaqBlock faqs={FAQS} />
      <SubHubGrid excludePath="/age-calculator/pet-dog-cat" />
    </Layout>
  );
}